import React, { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './SeasonalEffect.css';

const SEASON_COLORS = {
  spring: '#d85c8a', /* Rosa/Fucsia morbido */
  summer: '#a67c52',
  autumn: '#7a4a1e',
  winter: '#3a7fc1',
  none:   '#a67c52',
};

const SEASON_CONFIGS = {
  spring: { emojis: ['🌸', '🌺', '🌷', '🌸'], count: 6, type: 'stem' },
  summer: { emojis: ['🌻', '🌼', '🌻', '🌼'], count: 6, type: 'stem' },
  autumn: { emojis: ['🍂', '🍁', '🍃', '🍂', '🍁'],       count: 18, type: 'fall' },
  winter: { emojis: ['❄️', '❄️', '❄️', '❄️', '✦', '❄️'],  count: 24, type: 'fall' },
};

const SeasonalEffect = () => {
  const [season, setSeason] = useState('none');

  useEffect(() => {
    const ref = doc(db, 'site_settings', 'global');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) setSeason(snap.data().season || 'none');
    });
    return () => unsub();
  }, []);

  // Aggiorna il colore primario CSS in base alla stagione
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--primary-color',
      SEASON_COLORS[season] || SEASON_COLORS.none
    );
  }, [season]);

  const config = SEASON_CONFIGS[season];

  // Genera particelle una sola volta per stagione
  const particles = useMemo(() => {
    if (!config) return [];
    return Array.from({ length: config.count }, (_, i) => {
      if (config.type === 'stem') {
        const isLeft = i % 2 === 0;
        // Calcola in che fascia verticale posizionare il fiore per non farli accavallare
        const sideIndex = Math.floor(i / 2);
        const sideCount = config.count / 2;
        const segment = 80 / sideCount; 
        const top = 10 + (sideIndex * segment) + (Math.random() * (segment * 0.6));
        
        const rot = -20 + (Math.random() * 40);
        return {
          id: i,
          emoji: config.emojis[i % config.emojis.length],
          isLeft,
          top,
          rotation: rot,
          delay: Math.random() * 8, // sfalsati
          duration: 12 + Math.random() * 10 // ciclo completo in 12-22 secondi
        };
      } else {
        const left = Math.random() * 100;
        return {
          id: i,
          emoji: config.emojis[i % config.emojis.length],
          left,
          delay:    Math.random() * 12,
          duration: 8 + Math.random() * 9,
          size:     0.85 + Math.random() * 1.1,
        };
      }
    });
  }, [season]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!config) return null;

  return (
    <div className={`seasonal-effect effect-${config.type}`} aria-hidden="true">
      {particles.map(p => {
        if (config.type === 'stem') {
           return (
             <div
               key={p.id}
               className={`s-particle stem-particle ${p.isLeft ? 'left-side' : 'right-side'}`}
               style={{
                 top: `${p.top}%`,
                 '--rot': `${p.rotation}deg`,
                 '--duration': `${p.duration}s`,
                 animationDelay: `${p.delay}s`,
               }}
             >
               {p.isLeft ? (
                 <svg viewBox="0 0 100 100" width="80" height="80">
                   <path d="M 0 80 Q 40 80 60 50 T 80 20" fill="none" stroke="#2db14e" strokeWidth="5" strokeLinecap="round" />
                   <text x="80" y="25" fontSize="35" dominantBaseline="middle" textAnchor="middle">{p.emoji}</text>
                 </svg>
               ) : (
                 <svg viewBox="0 0 100 100" width="80" height="80">
                   <path d="M 100 80 Q 60 80 40 50 T 20 20" fill="none" stroke="#2db14e" strokeWidth="5" strokeLinecap="round" />
                   <text x="20" y="25" fontSize="35" dominantBaseline="middle" textAnchor="middle">{p.emoji}</text>
                 </svg>
               )}
             </div>
           );
        } else {
           return (
             <span
               key={p.id}
               className="s-particle"
               style={{
                 left:              `${p.left}%`,
                 animationDelay:    `${p.delay}s`,
                 animationDuration: `${p.duration}s`,
                 fontSize:          `${p.size}rem`,
               }}
             >
               {p.emoji}
             </span>
           );
        }
      })}
    </div>
  );
};

export default SeasonalEffect;
